/**
 * Hardware Product Versions hook using React Relay
 *
 * Fetches hardware product versions for a specific product.
 * Uses the nested hardwareProductVersions field on HardwareProduct.
 *
 * Note: Run `npm run relay` after making changes to GraphQL queries
 * to regenerate the type artifacts.
 */

import { useLazyLoadQuery, fetchQuery } from 'react-relay';
import { useState, useEffect, useCallback, useMemo } from 'react';
import graphql from 'babel-plugin-relay/macro';
import { relayEnvironment } from '../../../../relay';
import type { useHardwareProductVersionsRelayQuery as UseHardwareProductVersionsRelayQuery } from './__generated__/useHardwareProductVersionsRelayQuery.graphql';

// ============================================================================
// GraphQL Definitions
// ============================================================================

/**
 * Query for hardware product versions via the hardware product
 */
const HardwareProductVersionsQuery = graphql`
  query useHardwareProductVersionsRelayQuery($id: ID!) {
    getHardwareProduct(id: $id) {
      id
      hardwareProductVersions(sort: [{ field: NAME, order: ASC }]) {
        id
        oldId
        name
        hardwareProductId
      }
    }
  }
`;

// ============================================================================
// Types
// ============================================================================

/**
 * Hardware Product Version from GraphQL
 */
export interface HardwareProductVersionGraphQL {
  id: string;
  oldId?: string | null;
  name?: string | null;
  hardwareProductId: string;
}

// ============================================================================
// Hooks
// ============================================================================

/**
 * Hook for fetching hardware product versions using Relay with Suspense
 *
 * Uses Suspense - wrap with Suspense boundary
 *
 * @example
 * function HardwareVersionSelect({ productId }) {
 *   return (
 *     <Suspense fallback={<Loading />}>
 *       <HardwareVersionSelectContent productId={productId} />
 *     </Suspense>
 *   );
 * }
 *
 * function HardwareVersionSelectContent({ productId }) {
 *   const versions = useHardwareProductVersionsRelay(productId);
 *   return <Select options={versions.map(v => ({ value: v.id, label: v.name }))} />;
 * }
 */
export function useHardwareProductVersionsRelay(hardwareProductId: string) {
  const data = useLazyLoadQuery<UseHardwareProductVersionsRelayQuery>(
    HardwareProductVersionsQuery,
    { id: hardwareProductId }
  );

  return (data.getHardwareProduct?.hardwareProductVersions ??
    []) as HardwareProductVersionGraphQL[];
}

// ============================================================================
// Non-Suspense Hook (with loading state)
// ============================================================================

/**
 * Hook that provides React Query-like interface for hardware product versions
 *
 * This hook does NOT use Suspense - it provides isLoading, data, refetch etc.
 *
 * @example
 * const { data, isLoading } = useHardwareProductVersionsRelayQuery(productId);
 *
 * if (isLoading) return <Loading />;
 * return <Select options={data.map(v => ({ value: v.id, label: v.name }))} />;
 */
export function useHardwareProductVersionsRelayQuery(
  hardwareProductId: string | undefined,
  options?: {
    enabled?: boolean;
  }
) {
  const [data, setData] = useState<HardwareProductVersionGraphQL[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isFetched, setIsFetched] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [fetchCount, setFetchCount] = useState(0);

  const enabled = options?.enabled !== false && Boolean(hardwareProductId);

  useEffect(() => {
    if (!enabled || !hardwareProductId) {
      setIsLoading(false);
      setData([]);
      return;
    }

    let cancelled = false;

    const doFetch = async () => {
      try {
        setIsLoading(true);

      const result = await fetchQuery<UseHardwareProductVersionsRelayQuery>(
          relayEnvironment,
          HardwareProductVersionsQuery,
          { id: hardwareProductId }
        ).toPromise();

        if (cancelled) return;

        if (result) {
          const versions = (result.getHardwareProduct?.hardwareProductVersions ??
            []) as HardwareProductVersionGraphQL[];
          setData(versions);
          setError(null);
        }
      } catch (err) {
        if (cancelled) return;
        setError(
          err instanceof Error ? err : new Error('Failed to fetch product versions')
        );
      } finally {
        if (!cancelled) {
          setIsLoading(false);
          setIsFetched(true);
        }
      }
    };

    doFetch();

    return () => {
      cancelled = true;
    };
  }, [enabled, hardwareProductId, fetchCount]);

  const refetch = useCallback(() => {
    setFetchCount((c) => c + 1);
  }, []);

  return useMemo(
    () => ({
      data,
      isLoading,
      isFetched,
      error,
      refetch,
    }),
    [data, isLoading, isFetched, error, refetch]
  );
}
